import {
  IFeatureTourApi,
  IFeatureTourRepository,
  IFeatureTourStepApi,
  IGetFeatureToursParams,
} from "../../adapters/driven/repositories/featureTourRepository";

export interface IFeatureTourStep {
  stepId: string;
  targetId: string;
  title: string;
  description: string;
  placement?: string | null;
  sortOrder: number;
}

export interface IFeatureTour {
  featureName: string;
  title: string;
  appScope: string;
  routePattern?: string | null;
  clientSurface?: string | null;
  prerequisiteFeatureNames: string[];
  requiredClientCapabilities: string[];
  minimumMinutesSinceFirstLogin?: number | null;
  minimumMinutesSinceLastTourCompletion?: number | null;
  completedSteps: string[];
  steps: IFeatureTourStep[];
}

export interface IFeatureTourStatus {
  firstLoginAt?: string | null;
  lastTourCompletionAt?: string | null;
  completedFeatures: string[];
  completedStepsByFeature: Record<string, string[]>;
  eligibleFeatures: IFeatureTour[];
}

export interface ICompleteFeatureTourStepResult {
  featureName: string;
  stepId: string;
  completedVia: string;
  completedAt: string;
  alreadyCompleted: boolean;
}

export interface IFeatureTourServiceError {
  code: "GENERIC";
  message: string;
}

function mapStep(step: IFeatureTourStepApi): IFeatureTourStep {
  return {
    stepId: step.step_id,
    targetId: step.target_id,
    title: step.title,
    description: step.description,
    placement: step.placement,
    sortOrder: step.sort_order,
  };
}

function mapTour(tour: IFeatureTourApi): IFeatureTour {
  return {
    featureName: tour.feature_name,
    title: tour.title,
    appScope: tour.app_scope,
    routePattern: tour.route_pattern,
    clientSurface: tour.client_surface,
    prerequisiteFeatureNames: tour.prerequisite_feature_names ?? [],
    requiredClientCapabilities: tour.required_client_capabilities ?? [],
    minimumMinutesSinceFirstLogin: tour.minimum_minutes_since_first_login,
    minimumMinutesSinceLastTourCompletion:
      tour.minimum_minutes_since_last_tour_completion,
    completedSteps: tour.completed_steps ?? [],
    steps: (tour.steps ?? []).map(mapStep),
  };
}

function looksLikeDetailPayload(data: unknown): boolean {
  return !!(
    data &&
    typeof data === "object" &&
    "detail" in (data as Record<string, unknown>)
  );
}

export const FeatureTourServiceImplFactory = ({
  featureTourRepository,
}: {
  featureTourRepository: IFeatureTourRepository;
}) => {
  const getFeatureTours = async (
    params: IGetFeatureToursParams
  ): Promise<
    IControllerPromiseResponse<IFeatureTourStatus, IFeatureTourServiceError>
  > => {
    try {
      const data = await featureTourRepository.getFeatureTours(params);
      if (looksLikeDetailPayload(data)) {
        return {
          success: false,
          error: { code: "GENERIC", message: "Failed to fetch feature tours" },
        };
      }

      return {
        success: true,
        data: {
          firstLoginAt: data.first_login_at,
          lastTourCompletionAt: data.last_tour_completion_at,
          completedFeatures: data.completed_features ?? [],
          completedStepsByFeature: data.completed_steps_by_feature ?? {},
          eligibleFeatures: (data.eligible_features ?? []).map(mapTour),
        },
      };
    } catch (error: unknown) {
      console.error("[FeatureTourService] getFeatureTours error:", error);
      return {
        success: false,
        error: { code: "GENERIC", message: "Failed to fetch feature tours" },
      };
    }
  };

  const completeFeatureTourStep = async (payload: {
    featureName: string;
    stepId: string;
    completedVia: string;
  }): Promise<
    IControllerPromiseResponse<
      ICompleteFeatureTourStepResult,
      IFeatureTourServiceError
    >
  > => {
    try {
      const data = await featureTourRepository.completeFeatureTourStep(payload);
      if (looksLikeDetailPayload(data)) {
        return {
          success: false,
          error: {
            code: "GENERIC",
            message: "Failed to complete feature tour step",
          },
        };
      }
      return {
        success: true,
        data: {
          featureName: data.feature_name,
          stepId: data.step_id,
          completedVia: data.completed_via,
          completedAt: data.completed_at,
          alreadyCompleted: data.already_completed,
        },
      };
    } catch (error: unknown) {
      console.error("[FeatureTourService] complete step error:", error);
      return {
        success: false,
        error: {
          code: "GENERIC",
          message: "Failed to complete feature tour step",
        },
      };
    }
  };

  return {
    getFeatureTours,
    completeFeatureTourStep,
  };
};
